import { useEffect, useState } from 'react';
import { useStudent } from '../context/StudentContext';
import { preferencesApi } from '../api';
import { Settings, Save, Key, MapPin, BookOpen, Check, X, GraduationCap, Ban, School } from 'lucide-react';

interface PreferenceData {
  target_cities: string[];
  target_majors: string[];
  school_type_preference: string;
  exclude_cities: string[];
  gugukey: string;
  // 初中偏好
  target_school_type: string;
  target_district: string[];
  target_grade: string;
}

const allCities = [
  '北京', '上海', '广州', '深圳', '杭州', '南京', '武汉', '成都', '西安', '重庆',
  '天津', '苏州', '厦门', '福州', '长沙', '郑州', '合肥', '济南', '青岛', '大连',
  '哈尔滨', '长春', '沈阳', '石家庄', '太原', '南昌', '南宁', '贵阳', '昆明', '兰州'
];

const allInterests = [
  '计算机类', '电子信息类', '机械类', '土木类', '建筑类', '经济管理类', '金融类',
  '医学类', '药学类', '法学类', '语言文学类', '新闻传播类', '艺术类', '教育类',
  '数学类', '物理类', '化学类', '生物类', '材料类', '能源类', '环境类'
];

const schoolTypes = [
  { value: '', label: '不限' },
  { value: '985', label: '优先985' },
  { value: '211', label: '优先211' },
  { value: 'dual', label: '优先双一流' },
];

// 初中高中类型
const juniorSchoolTypes = [
  { value: '', label: '不限' },
  { value: '一双外', label: '一双外（一中/双十/外国语）' },
  { value: '普高', label: '普通高中' },
  { value: '示范', label: '示范高中' },
  { value: '职高', label: '职业高中' },
];

// 偏好字段标签映射
const FIELD_LABELS: Record<string, { title: string; icon: string; desc: string }> = {
  target_cities: { title: '意向城市', icon: '📍', desc: '选择感兴趣的城市，推荐时会优先考虑这些地区的院校' },
  target_majors: { title: '感兴趣的专业方向', icon: '📚', desc: '选择感兴趣的专业类别，系统会优先推荐相关专业' },
  school_tier: { title: '院校类型偏好', icon: '🎓', desc: '更倾向报考的院校类型' },
  subject_type_preference: { title: '科目类型偏好', icon: '📖', desc: '选择科目类型偏好' },
  target_school_type: { title: '高中类型偏好', icon: '🏫', desc: '选择倾向的高中类型' },
  target_district: { title: '意向区域', icon: '📍', desc: '选择感兴趣的区域，推荐时会优先考虑' },
  target_grade: { title: '目标等级', icon: '🎯', desc: '设置目标升学等级' },
};

export default function Preferences() {
  const { currentStudent } = useStudent();
  const [preferences, setPreferences] = useState<PreferenceData>({
    target_cities: [],
    target_majors: [],
    school_type_preference: '',
    exclude_cities: [],
    gugukey: '',
    target_school_type: '',
    target_district: [],
    target_grade: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [schema, setSchema] = useState<string[]>([]);
  const [educationLevel, setEducationLevel] = useState<'senior' | 'junior'>('senior');

  const allDistricts = ['鼓楼区', '台江区', '仓山区', '马尾区', '晋安区', '闽侯县', '连江县', '罗源县', '闽清县', '永泰县', '平潭县', '福清市', '长乐区'];

  useEffect(() => {
    if (!currentStudent) return;
    loadPreferences();
    loadSchema();
  }, [currentStudent?.id]);

  const loadSchema = async () => {
    if (!currentStudent) return;
    try {
      const res = await preferencesApi.schema(currentStudent.id);
      if (res.data.data) {
        setSchema(res.data.data);
        setEducationLevel(res.data.education_level || 'senior');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const loadPreferences = async () => {
    if (!currentStudent) return;
    setLoading(true);
    try {
      const res = await preferencesApi.get(currentStudent.id);
      const data = res.data.data || {};
      setPreferences({
        target_cities: data.target_cities || [],
        target_majors: data.target_majors || [],
        school_type_preference: data.school_type_preference || '',
        exclude_cities: data.exclude_cities || [],
        gugukey: data.gugukey || '',
        target_school_type: data.target_school_type || '',
        target_district: data.target_district || [],
        target_grade: data.target_grade || '',
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!currentStudent) return;
    setSaving(true);
    setMessage(null);
    try {
      const prefs: Record<string, unknown> = {};
      schema.forEach(key => {
        prefs[key] = (preferences as any)[key];
      });
      // 始终保存通用字段
      prefs.gugukey = preferences.gugukey;

      await preferencesApi.batch(currentStudent.id, prefs);
      setMessage({ type: 'success', text: '设置已保存' });
    } catch (e) {
      console.error(e);
      setMessage({ type: 'error', text: '保存失败，请重试' });
    } finally {
      setSaving(false);
    }
  };

  const toggleCity = (city: string) => {
    setPreferences(prev => ({
      ...prev,
      target_cities: prev.target_cities.includes(city)
        ? prev.target_cities.filter(c => c !== city)
        : [...prev.target_cities, city]
    }));
  };

  const toggleExcludeCity = (city: string) => {
    setPreferences(prev => ({
      ...prev,
      exclude_cities: prev.exclude_cities.includes(city)
        ? prev.exclude_cities.filter(c => c !== city)
        : [...prev.exclude_cities, city]
    }));
  };

  const toggleInterest = (interest: string) => {
    setPreferences(prev => ({
      ...prev,
      target_majors: prev.target_majors.includes(interest)
        ? prev.target_majors.filter(i => i !== interest)
        : [...prev.target_majors, interest]
    }));
  };

  const toggleDistrict = (district: string) => {
    setPreferences(prev => ({
      ...prev,
      target_district: prev.target_district.includes(district)
        ? prev.target_district.filter(d => d !== district)
        : [...prev.target_district, district]
    }));
  };

  if (loading) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-64 bg-gray-200 rounded-2xl"></div>
          <div className="h-48 bg-gray-200 rounded-2xl"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 md:p-6 max-w-3xl mx-auto">
      {/* 顶部标题 */}
      <div className="mb-4 md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-800">偏好设置</h1>
        <p className="text-gray-500 text-sm mt-1">
          设置 {currentStudent?.name} 的意向城市和偏好，{educationLevel === 'junior' ? '高中推荐' : '个性化推荐'}更精准
          <span className="ml-2 text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">
            {educationLevel === 'junior' ? '初中' : '高中'}
          </span>
        </p>
      </div>

      {/* 消息提示 */}
      {message && (
        <div className={`mb-4 p-3 rounded-xl text-sm flex items-center gap-2 ${
          message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          {message.type === 'success' ? <Check size={16} /> : <X size={16} />}
          {message.text}
        </div>
      )}

      {/* 学段偏好项 */}
      {schema.includes('target_cities') && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <MapPin size={20} className="text-blue-500" />
            <h2 className="font-semibold text-gray-800">意向城市</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">{FIELD_LABELS.target_cities.desc}</p>
          <div className="flex flex-wrap gap-2">
            {allCities.map(city => (
              <button
                key={city}
                onClick={() => toggleCity(city)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
                  preferences.target_cities.includes(city)
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {city}
                {preferences.target_cities.includes(city) && <Check size={12} className="inline ml-1" />}
              </button>
            ))}
          </div>
          {preferences.target_cities.length > 0 && (
            <p className="text-xs text-gray-400 mt-3">已选择 {preferences.target_cities.length} 个城市</p>
          )}
        </div>
      )}

      {schema.includes('exclude_cities') && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Ban size={20} className="text-red-500" />
            <h2 className="font-semibold text-gray-800">排除城市</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">不想去的城市（志愿推荐时会排除这些地区）</p>
          <div className="flex flex-wrap gap-2">
            {allCities.map(city => (
              <button
                key={city}
                onClick={() => toggleExcludeCity(city)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
                  preferences.exclude_cities.includes(city)
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {city}
                {preferences.exclude_cities.includes(city) && <X size={12} className="inline ml-1" />}
              </button>
            ))}
          </div>
          {preferences.exclude_cities.length > 0 && (
            <p className="text-xs text-gray-400 mt-3">已排除 {preferences.exclude_cities.length} 个城市</p>
          )}
        </div>
      )}

      {schema.includes('school_tier') && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <GraduationCap size={20} className="text-purple-500" />
            <h2 className="font-semibold text-gray-800">院校类型偏好</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">更倾向报考的院校类型</p>
          <div className="flex flex-wrap gap-2">
            {schoolTypes.map(type => (
              <button
                key={type.value}
                onClick={() => setPreferences(prev => ({ ...prev, school_type_preference: type.value }))}
                className={`px-4 py-2 text-sm rounded-lg transition-all ${
                  preferences.school_type_preference === type.value
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {schema.includes('target_majors') && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen size={20} className="text-purple-500" />
            <h2 className="font-semibold text-gray-800">感兴趣的专业方向</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">{FIELD_LABELS.target_majors.desc}</p>
          <div className="flex flex-wrap gap-2">
            {allInterests.map(interest => (
              <button
                key={interest}
                onClick={() => toggleInterest(interest)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
                  preferences.target_majors.includes(interest)
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {interest}
                {preferences.target_majors.includes(interest) && <Check size={12} className="inline ml-1" />}
              </button>
            ))}
          </div>
          {preferences.target_majors.length > 0 && (
            <p className="text-xs text-gray-400 mt-3">已选择 {preferences.target_majors.length} 个专业方向</p>
          )}
        </div>
      )}

      {/* 初中专属偏好 */}
      {schema.includes('target_school_type') && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <School size={20} className="text-purple-500" />
            <h2 className="font-semibold text-gray-800">高中类型偏好</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">选择倾向的高中类型</p>
          <div className="flex flex-wrap gap-2">
            {juniorSchoolTypes.map(type => (
              <button
                key={type.value}
                onClick={() => setPreferences(prev => ({ ...prev, target_school_type: type.value }))}
                className={`px-4 py-2 text-sm rounded-lg transition-all ${
                  preferences.target_school_type === type.value
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {schema.includes('target_district') && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <MapPin size={20} className="text-blue-500" />
            <h2 className="font-semibold text-gray-800">意向区域</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">选择感兴趣的区域，推荐时会优先考虑</p>
          <div className="flex flex-wrap gap-2">
            {allDistricts.map(district => (
              <button
                key={district}
                onClick={() => toggleDistrict(district)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
                  preferences.target_district.includes(district)
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {district}
                {preferences.target_district.includes(district) && <Check size={12} className="inline ml-1" />}
              </button>
            ))}
          </div>
          {preferences.target_district.length > 0 && (
            <p className="text-xs text-gray-400 mt-3">已选择 {preferences.target_district.length} 个区域</p>
          )}
        </div>
      )}

      {/* API配置 */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Key size={20} className="text-amber-500" />
          <h2 className="font-semibold text-gray-800">数据源配置</h2>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          咕咕数据 API Key（可选）。用于补充掌上高考未能覆盖的院校数据，仅在发现数据缺口时按需使用。
        </p>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">咕咕数据 API Key</label>
          <input
            type="text"
            value={preferences.gugukey}
            onChange={e => setPreferences({ ...preferences, gugukey: e.target.value })}
            placeholder="请输入 API Key（以 gk_ 开头）"
            className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 font-mono"
          />
          <p className="text-xs text-gray-400 mt-2">
            申请地址：<a href="https://www.ggdata.com/" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">咕咕数据官网</a>
            （注册后可在个人中心查看 API Key）
          </p>
        </div>
      </div>

      {/* 保存按钮 */}
      <div className="flex items-center justify-end gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          <Save size={16} />
          {saving ? '保存中...' : '保存设置'}
        </button>
      </div>
    </div>
  );
}
